// src/utils/excel.ts
import * as XLSX from "xlsx";

export type ExcelRow = {
  idproducto?: string | null;
  troquel?: string | null;
  codebar?: string | null;
  product?: string | null;
  lab?: string | null;
  rubro?: string | null;
  cantidad?: number | null;
  pvp?: number | null;
  sin_impuestos?: number | null;
};

const HEADER_ALIASES: Record<keyof ExcelRow, string[]> = {
  idproducto: ["idproducto", "id producto", "id", "codigo interno", "código interno"],
  troquel: ["troquel"],
  codebar: ["codebar", "codigo de barras", "código de barras", "codigo_barra", "ean", "barcode"],
  product: ["producto", "descripcion", "descripción", "presentacion", "presentación"],
  lab: ["laboratorio", "lab"],
  rubro: ["rubro", "categoria", "categoría"],
  cantidad: ["cantidad", "stock", "existencia", "cant"],
  pvp: ["pvp", "precio", "precio lista", "precio venta publico", "precio venta público", "precio final"],
  sin_impuestos: ["sin impuesto", "sin impuestos", "neto", "precio neto"],
};

// Normaliza las claves del encabezado
const norm = (s: any) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

// ----------------------------------------------------------
// NUEVO numAR() → interpreta correctamente formatos mixtos
// ----------------------------------------------------------
const numAR = (v: any): number | null => {
  if (v == null || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;

  let s = String(v)
    .replace(/[^\d,.\-]+/g, "")
    .replace(/\u00A0/g, "") // elimina espacio duro
    .trim();
  if (!s) return null;

  const hasComma = s.includes(",");
  const hasDot = s.includes(".");

  if (hasComma && hasDot) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    const decimalIsComma = lastComma > lastDot;
    if (decimalIsComma) {
      // 55.965,00 -> 55965.00
      s = s.replace(/\./g, "").replace(",", ".");
    } else {
      // 55,965.00 -> 55965.00
      s = s.replace(/,/g, "");
    }
  } else if (hasComma) {
    // 12,34 -> 12.34
    s = s.replace(",", ".");
  } else if (hasDot) {
    // Detectar si el punto separa miles
    const parts = s.split(".");
    if (parts.length > 1 && parts[parts.length - 1].length !== 3) {
      // último bloque no tiene 3 → decimal real (12.5)
    } else {
      // puntos como miles -> quitar
      s = s.replace(/\./g, "");
    }
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

// ----------------------------------------------------------
// Lector principal de Excel
// ----------------------------------------------------------
export async function readPriceFile(file: File): Promise<ExcelRow[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw: any[] = XLSX.utils.sheet_to_json(ws, { defval: null, raw: false });
  if (!raw.length) return [];

  const firstRow = raw[0] as Record<string, unknown>;
  const headerIndex: Record<string, string> = {};
  Object.keys(firstRow).forEach((k) => (headerIndex[norm(k)] = k));

  const map: Record<keyof ExcelRow, string | undefined> = {} as any;
  (Object.keys(HEADER_ALIASES) as (keyof ExcelRow)[]).forEach((key) => {
    for (const alias of HEADER_ALIASES[key]) {
      const real = headerIndex[norm(alias)];
      if (real) {
        map[key] = real;
        break;
      }
    }
  });

  const pick = (row: any, key: keyof ExcelRow) => {
    const col = map[key];
    const v = col ? row[col] : null;
    return v == null ? null : String(v).trim();
  };

  return raw.map((r) => ({
    idproducto: pick(r, "idproducto"),
    troquel: pick(r, "troquel"),
    codebar: pick(r, "codebar"),
    product: pick(r, "product"),
    lab: pick(r, "lab"),
    rubro: pick(r, "rubro"),
    cantidad: numAR(r[map.cantidad!]),
    pvp: numAR(r[map.pvp!]),
    sin_impuestos: numAR(r[map.sin_impuestos!]),
  }));
}
